import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SegmentedChoice } from '../../../components/design-system/common/SegmentedChoice';
import { SettingsField } from '../../../components/design-system/common/SettingsPanel';
import {
    PRODUCT_INFO_FIELDS,
    ProductInfoField,
    ROOM_ZONES,
    RoomZone,
    WidgetDisplay,
    WidgetPlacement,
} from './layout.schema';
import { FIELD_LABEL_KEYS, WIDGET_META, ZONE_LABEL_KEYS } from './layout.meta';

/**
 * Everything about one widget: whether it shows, where, how a button is drawn,
 * and — for the product info — which fields and in what order.
 */
export function WidgetInspector({
    placement,
    onChange,
    onMove,
    onClose,
}: {
    placement: WidgetPlacement;
    onChange: (patch: Partial<WidgetPlacement>) => void;
    onMove: (zone: RoomZone) => void;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    const meta = WIDGET_META[placement.id];
    const Icon = meta.icon;

    // Fields in the vendor's order, then the ones not shown, so any can be added.
    const chosen = placement.fields ?? [];
    const allFields: ProductInfoField[] = [...chosen, ...PRODUCT_INFO_FIELDS.filter((f) => !chosen.includes(f))];
    const toggleField = (field: ProductInfoField, on: boolean) => {
        const next = on ? allFields.filter((f) => f === field || chosen.includes(f)) : chosen.filter((f) => f !== field);
        if (next.length) onChange({ fields: next });
    };
    const moveField = (field: ProductInfoField, dir: -1 | 1) => {
        const i = chosen.indexOf(field);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= chosen.length) return;
        const next = [...chosen];
        [next[i], next[j]] = [next[j], next[i]];
        onChange({ fields: next });
    };

    return (
        <div className="flex flex-col gap-4 rounded-lg border bg-background p-3.5">
            <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-2.5">
                    <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                        <Icon className="size-4" />
                    </span>
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground">{t(meta.labelKey)}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{t(meta.descKey)}</p>
                    </div>
                </div>
                <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label={t('VISUALIZER_LAYOUT.INSPECTOR.CLOSE')}>
                    <X className="size-4" />
                </Button>
            </div>

            <div className="flex items-center justify-between gap-4">
                <Label htmlFor={`widget-visible-${placement.id}`} className="text-sm font-normal text-foreground">
                    {t('VISUALIZER_LAYOUT.INSPECTOR.SHOW')}
                </Label>
                <Switch
                    id={`widget-visible-${placement.id}`}
                    checked={placement.visible}
                    onCheckedChange={(visible) => onChange({ visible })}
                />
            </div>

            <SettingsField label={t('VISUALIZER_LAYOUT.INSPECTOR.POSITION')} htmlFor={`widget-zone-${placement.id}`}>
                <Select value={placement.zone} onValueChange={(z) => onMove(z as RoomZone)}>
                    <SelectTrigger id={`widget-zone-${placement.id}`} className="w-full">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {ROOM_ZONES.map((z) => (
                            <SelectItem key={z} value={z}>{t(ZONE_LABEL_KEYS[z])}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </SettingsField>

            {meta.isButton && (
                <SettingsField label={t('VISUALIZER_LAYOUT.INSPECTOR.DISPLAY')}>
                    <SegmentedChoice<WidgetDisplay>
                        value={placement.display ?? 'icon-label'}
                        onChange={(display) => onChange({ display })}
                        options={[
                            { value: 'icon-label', label: t('VISUALIZER_LAYOUT.DISPLAY.ICON_LABEL') },
                            { value: 'icon', label: t('VISUALIZER_LAYOUT.DISPLAY.ICON') },
                            { value: 'label', label: t('VISUALIZER_LAYOUT.DISPLAY.LABEL') },
                        ]}
                    />
                </SettingsField>
            )}

            {placement.id === 'productInfo' && (
                <SettingsField label={t('VISUALIZER_LAYOUT.INSPECTOR.FIELDS')} hint={t('VISUALIZER_LAYOUT.INSPECTOR.FIELDS_HINT')}>
                    <ul className="flex flex-col divide-y rounded-md border">
                        {allFields.map((field) => {
                            const on = chosen.includes(field);
                            const index = chosen.indexOf(field);
                            const id = `product-info-field-${field}`;
                            return (
                                <li key={field} className={cn('flex items-center gap-2.5 px-2.5 py-1.5', !on && 'bg-muted/40')}>
                                    <Checkbox id={id} checked={on} onCheckedChange={(v) => toggleField(field, v === true)} disabled={on && chosen.length === 1} />
                                    <Label htmlFor={id} className={cn('flex-1 text-sm font-normal', on ? 'text-foreground' : 'text-muted-foreground')}>
                                        {t(FIELD_LABEL_KEYS[field])}
                                    </Label>
                                    {on && (
                                        <span className="flex items-center gap-0.5">
                                            <Button variant="ghost" size="icon-xs" disabled={index === 0} onClick={() => moveField(field, -1)} aria-label={t('VISUALIZER_LAYOUT.INSPECTOR.MOVE_UP')}>
                                                <ArrowUp className="size-3.5" />
                                            </Button>
                                            <Button variant="ghost" size="icon-xs" disabled={index === chosen.length - 1} onClick={() => moveField(field, 1)} aria-label={t('VISUALIZER_LAYOUT.INSPECTOR.MOVE_DOWN')}>
                                                <ArrowDown className="size-3.5" />
                                            </Button>
                                        </span>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </SettingsField>
            )}
        </div>
    );
}
